/**
 * AssemblyProgress — LIVE job-status board (CR-2026-SFM-UPLOAD-ROUTING).
 *
 * For a job, shows released assemblies grouped over their members, each with a
 * % complete derived from current_stage (CUT 20 → FIT 40 → WELD 60 → QC 80 →
 * READY_TO_SHIP/SHIPPED 100), rolled up per assembly and overall. Read-only.
 * Data comes from the fabrication_events-backed stage on each piece and
 * auto-refreshes every 25s while Live is on, so it can be left on a monitor.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import PageGuide from "../../components/PageGuide";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import {
  Box, Container, Card, Typography, Stack, Select, MenuItem, FormControl,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Collapse, IconButton,
  LinearProgress, Alert, Switch, FormControlLabel, Tooltip, Divider,
} from "@mui/material";
import {
  ExpandMore, ExpandLess, InsightsRounded, RefreshRounded,
  LocalShippingRounded, DoneAllRounded, PrecisionManufacturingRounded,
} from "@mui/icons-material";
import { getJobs } from "../../feature/jobSlice";
import { getAssemblyProgress } from "../../utils/routingApi";
import { showErrorToast } from "../../utils/Toast";

function StageChip({ stage }) {
  const map = {
    CUT: "default", FIT: "info", WELD: "primary", QC: "warning",
    READY_TO_SHIP: "success", SHIPPED: "success",
  };
  return <Chip size="small" label={stage || "—"} color={map[stage] || "default"} variant={stage ? "filled" : "outlined"} />;
}

function PctBar({ pct }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 160 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
      </Box>
      <Typography variant="caption" sx={{ width: 36, textAlign: "right" }}>{pct}%</Typography>
    </Stack>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <Card variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, color: color || "text.secondary" }}>
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "text.secondary" }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 800, color: color || "text.primary", lineHeight: 1.1 }}>{value}</Typography>
    </Card>
  );
}

const fmtTime = (d) =>
  d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";

export default function AssemblyProgress() {
  const dispatch = useDispatch();
  const jobs = useSelector((s) => s.jobSlice?.jobs || []);
  const [jobId, setJobId] = useState("");
  const [data, setData] = useState({ assemblies: [], overallPct: 0, count: 0 });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [live, setLive] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => { dispatch(getJobs()); }, [dispatch]);

  const load = useCallback(async (jid, { silent = false } = {}) => {
    if (!jid) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      setData(await getAssemblyProgress(jid));
      setLastUpdated(new Date());
    } catch (e) {
      if (!silent) showErrorToast(e?.response?.data?.message || "Failed to load progress");
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, []);

  // Initial / job-change load
  useEffect(() => { if (jobId) load(jobId); }, [jobId, load]);

  // Live auto-refresh (every 25s while Live is on and a job is selected)
  useEffect(() => {
    if (!jobId || !live) return undefined;
    const id = setInterval(() => load(jobId, { silent: true }), 25000);
    return () => clearInterval(id);
  }, [jobId, live, load]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // Stage tally across leaf pieces (members where present, else the assembly)
  const tally = useMemo(() => {
    const t = { CUT: 0, FIT: 0, WELD: 0, QC: 0, READY_TO_SHIP: 0, SHIPPED: 0 };
    (data.assemblies || []).forEach((a) => {
      const leaves = a.members && a.members.length ? a.members : [a];
      leaves.forEach((l) => {
        const q = Number(l.quantity) || 0;
        if (t[l.current_stage] !== undefined) t[l.current_stage] += q;
      });
    });
    return t;
  }, [data]);

  const inFab = tally.CUT + tally.FIT + tally.WELD + tally.QC;

  return (
    <>
      <Helmet><title>Assembly Progress | SFM</title></Helmet>
      <Container>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <InsightsRounded color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Assembly Progress</Typography>
          <Box sx={{ flex: 1 }} />
          {jobId && live && (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{
                width: 9, height: 9, borderRadius: "50%", bgcolor: "success.main",
                animation: "sfmpulse 1.4s ease-in-out infinite",
                "@keyframes sfmpulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.25 } },
              }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: "success.main", letterSpacing: "0.06em" }}>LIVE</Typography>
            </Stack>
          )}
        </Stack>

        <PageGuide
          why="See how far along each assembly is at a glance — so you can spot what's behind before it becomes a shipping problem."
          who="PMs and supervisors watching schedule; the QM spotting slippage."
          what="A live progress view of assemblies and their pieces across a job."
          how="Pick a job to see each assembly's status and completion."
          when="Any time you need a quick read on shop progress for a job."
          standard="AISC 303 §4"
        />

        <Card sx={{ p: 3 }}>
          {/* Controls row */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <Select value={jobId} onChange={(e) => setJobId(e.target.value)} displayEmpty>
                <MenuItem value="" disabled><em>Select Job</em></MenuItem>
                {jobs.map((j) => <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>)}
              </Select>
            </FormControl>
            {jobId && <Chip label={`${data.count} released piece${data.count !== 1 ? "s" : ""}`} variant="outlined" />}
            <Box sx={{ flex: 1 }} />
            {jobId && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Updated {fmtTime(lastUpdated)}
                </Typography>
                <Tooltip title="Refresh now">
                  <span>
                    <IconButton size="small" onClick={() => load(jobId, { silent: true })} disabled={loading || refreshing}>
                      <RefreshRounded sx={{ animation: refreshing ? "sfmspin 0.8s linear infinite" : "none", "@keyframes sfmspin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <FormControlLabel
                  control={<Switch size="small" checked={live} onChange={(e) => setLive(e.target.checked)} />}
                  label={<Typography variant="body2">Live</Typography>}
                  sx={{ mr: 0 }}
                />
              </>
            )}
          </Stack>

          {/* KPI summary strip */}
          {jobId && data.count > 0 && (
            <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", rowGap: 2 }}>
              <StatCard icon={<InsightsRounded fontSize="small" />} label="Overall" value={`${data.overallPct}%`} color="primary.main" />
              <StatCard icon={<PrecisionManufacturingRounded fontSize="small" />} label="In Fabrication" value={inFab} />
              <StatCard icon={<LocalShippingRounded fontSize="small" />} label="Ready to Ship" value={tally.READY_TO_SHIP} color="success.main" />
              <StatCard icon={<DoneAllRounded fontSize="small" />} label="Shipped" value={tally.SHIPPED} color="success.main" />
            </Stack>
          )}

          {/* Overall progress bar */}
          {jobId && data.count > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Job completion</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "primary.main" }}>{data.overallPct}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={data.overallPct} sx={{ height: 12, borderRadius: 6 }} />
            </Box>
          )}

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {!loading && jobId && data.count === 0 && (
            <Alert severity="info">No released assemblies yet for this job. Release for Fabrication first.</Alert>
          )}

          {!jobId && (
            <Alert severity="info">Select a job to see live fabrication progress.</Alert>
          )}

          {data.count > 0 && (
            <>
              <Divider sx={{ mb: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell sx={{ fontWeight: 700 }}>Assembly / Member</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 200 }}>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.assemblies.map((a) => (
                    <React.Fragment key={a.id}>
                      <TableRow hover sx={{ bgcolor: "action.hover" }}>
                        <TableCell>
                          {(a.members?.length || 0) > 0 && (
                            <IconButton size="small" onClick={() => toggle(a.id)}>
                              {expanded[a.id] ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{a.name}</TableCell>
                        <TableCell><StageChip stage={a.current_stage} /></TableCell>
                        <TableCell align="right">{a.quantity}</TableCell>
                        <TableCell><PctBar pct={a.pct} /></TableCell>
                      </TableRow>
                      {(a.members?.length || 0) > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                            <Collapse in={!!expanded[a.id]} unmountOnExit>
                              <Table size="small">
                                <TableBody>
                                  {a.members.map((m) => (
                                    <TableRow key={m.id}>
                                      <TableCell width={40} />
                                      <TableCell sx={{ pl: 4 }}>{m.name} <Typography variant="caption" color="text.secondary">{m.section}</Typography></TableCell>
                                      <TableCell><StageChip stage={m.current_stage} /></TableCell>
                                      <TableCell align="right">{m.quantity}</TableCell>
                                      <TableCell><PctBar pct={m.pct} /></TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Card>
      </Container>
    </>
  );
}
